import { useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { Grid, useTheme } from "@mui/material";
import { CrewScheduleGL } from "src/pages/CrewSchedule/gl/CrewScheduleGL";

interface IBidPackageTimeScaleProps {
  id: string;
  path: string[];
  timeScaleStart: number | null;
  timeScaleEnd: number | null;
}

/**
 * Renders a standard time scale using a buffered (by 1 day) start and end time using bidWindowOpen and bidBlockEnd
 * @param props -
 * @returns
 */
export const BidPackageTimeScaleCanvas = (props: IBidPackageTimeScaleProps): JSX.Element => {
  const mount = useRef<HTMLDivElement>(null);
  const frameIdRef = useRef<number>(0);
  const { path, timeScaleStart, timeScaleEnd } = props;

  const theme = useTheme();
  const crewSchedGL = useRef<CrewScheduleGL | null>(null);

  const animate = useCallback(() => {
    if (crewSchedGL?.current) crewSchedGL.current.draw();
  }, []);

  const start = useCallback(() => {
    frameIdRef.current = requestAnimationFrame(animate);
  }, [animate]);

  useEffect(() => {
    return () => {
      if (crewSchedGL?.current?.loseContextExt) {
        // The following will remove the webgl context on unmount if extension is available.
        // console.log("lose webgl context");
        crewSchedGL.current.loseContextExt.loseContext();
      }
    };
  }, []);

  useLayoutEffect(() => {
    crewSchedGL.current = new CrewScheduleGL(mount.current, 0, 75); // width will be dynamically set, height is locked at 75.
  }, []);

  useEffect(() => {
    crewSchedGL?.current?.setTheme(theme.custom.timeline.webglCanvas);
  }, [theme]);

  useEffect(() => {
    if (!!timeScaleStart && !!timeScaleEnd) {
      crewSchedGL?.current?.setTimeAxis(timeScaleStart, timeScaleEnd);
      start();
    }
  }, [timeScaleStart, timeScaleEnd, start]);

  const canvasDim = useRef<DOMRect>(mount.current?.getBoundingClientRect());

  useLayoutEffect(() => {
    setTimeout(() => {
      const newDim = mount?.current?.getBoundingClientRect();
      if (!newDim || newDim.width === 0) {
        return;
      }
      if (newDim.width !== canvasDim.current?.width) {
        canvasDim.current = newDim;
        crewSchedGL.current?.setViewport(newDim.width, 75);
        start();
      }
    }, 0);
  }, [path, canvasDim, crewSchedGL, mount, start]);

  return (
    <Grid item className="bidpackage_time_display_canvas">
      <div className="timeDisplayGL" ref={mount} id={props.id + "-timeDisplayGL"}></div>
    </Grid>
  );
};
